/**
 * GENERAL NOTES:
 *
 * This is where we place most of our Attachment display utilities, but our Uploader widget in ../shared/forms/uploader/index.js. There
 * are a few specific uploader attachment components there too, but the widely used Attachment components are in this file.
 *
 * Similar to the uploader widget, the variety of use cases for attachment displays makes this file a bit long and complicated. However,
 * the general philosophy of this file is to lean towards duplicative code, rather than complex abstraction... meaning we have a lot of different
 * types of attachments, but they aren't very complicated.
 *
 * The most common issue for these attachment components is to handle both image & non image attachments gracefully, with image expansion utilities.
 */

import React from "react"
import * as Sentry from "@sentry/react"
import {CustomerImageExpander, CustomerVideoExpander, ImageExpander, VideoExpander, classNames} from 'js/studios/shared/utils'
import {truncateSimple} from 'js/studios/shared/utils/strings'
import {ProgressBar} from './progress_bar'

export function sortSavedAttachments(a, b) {
  if (a.isImage && b.isImage) {
    if (!a.id && !b.id) {
      return 0
    } else if (a.id && b.id) {
      return a.id - b.id
    } else if (a.id) {
      return -1
    } else {
      return 1
    }
  } else if (a.isImage) {
    return -1
  } else if (b.isImage) {
    return 1
  } else {
    return 0
  }
}

export function sortUploads(a, b) {
  if (a.isImage && b.isImage) {
    if (!a.data && !b.data) {
      return 0
    } else if (a.data && b.data) {
      return a.data.id - b.data.id
    } else if (a.data) {
      return -1
    } else {
      return 1
    }
  } else if (a.isImage) {
    return -1
  } else if (b.isImage) {
    return 1
  } else {
    return 0
  }
}


export class Attachment extends React.Component {

  render() {
    let content = null
    const srcUrl = this.props.srcUrl || this.props.attachment.smallUrl
    const alt = this.props.alt || ""

    const style = this.props.style || {}
    const imgStyle = {}
    if (this.props.height) {
      style.height = `${this.props.height}px`
      imgStyle.height = `${this.props.height}px`
    }
    if (this.props.width) {
      style.width = `${this.props.width}px`
      imgStyle.width = `${this.props.width}px`
    }

    if (this.props.attachment.videoUrl) {
      content = <div className="attachment_wrapper">
        <VideoExpander url={this.props.attachment.videoUrl} thumbUrl={this.props.attachment.smallUrl} />
        <img src={this.props.attachment.smallUrl} height={this.props.height} width={this.props.width} style={imgStyle} alt={alt} />
      </div>
    } else if (this.props.attachment.isImage) {
      const overlay = this.props.hideOverlay ? null : (this.props.overlay || <ImageExpander href={this.props.attachment.fullUrl} downloadHref={this.props.attachment.originalDownloadUrl} imageGroup={this.props.imageGroup} />)
      content = <div className="attachment_wrapper">
        {overlay}
        <img src={srcUrl} height={this.props.height} width={this.props.width} style={imgStyle} alt={alt} />
      </div>
    } else {
      if (!this.props.attachment.filename) {
        Sentry.withScope(scope => {
          scope.setLevel("warning")
          scope.setExtras({ attachment: this.props.attachment })
          Sentry.captureMessage("Attachment: Unexpected missing filename on non-image attachment")
        })
      }
      const trailing = this.props.attachment.filename ? this.props.attachment.filename.split('.')[1] : '' 
      content = <a className="attachment_wrapper" href={this.props.attachment.fullUrl} download={this.props.attachment.filename}>
        <img src={`${window.cm.structureUrl}/static/library/images/job_new/attachment.gif`} height={this.props.height} width={this.props.width} style={imgStyle} alt={alt} />
        <span className="attachment_download icon-down-dir">{truncateSimple(this.props.attachment.filename, 13, trailing)}</span>
      </a>
    }

    const TagName = this.props.tagName || 'li'
    return <TagName className={classNames("attachment", {[this.props.className]: this.props.className})} style={style}>
      {content}
      {this.props.children}
    </TagName>
  }
}

export class CustomerAttachment extends React.Component {

  render() {
    var attachment = this.props.attachment, content
    var TagName = this.props.tagName || 'li'
    var srcUrl = this.props.srcUrl || attachment.smallUrl
    var height = this.props.height
    var width = this.props.width

    var style = this.props.style || {}
    var imgStyle = {}
    if (height) {
      style.height = height + "px"
      imgStyle.height = height + "px"
    }

    if (width) {
      style.width = width + "px"
      imgStyle.width = width + "px"
    }

    if (attachment.videoUrl) {
      content = (
        <div className="attachment-wrapper">
          <CustomerVideoExpander href={attachment.videoUrl} thumbUrl={attachment.smallUrl} />
          <img src={attachment.smallUrl} />
        </div>
      )
    } else if (attachment.isImage) {
      if (this.props.overlay) {
        content = (
          <div className="attachment-wrapper">
            {this.props.overlay}
            <img src={srcUrl} height={height} width={width} style={imgStyle} />
          </div>
        )
      } else {
        content = (
          <div className="attachment-wrapper">
            <CustomerImageExpander href={attachment.fullUrl}>
              <img src={srcUrl} height={height} width={width} style={imgStyle} />
            </CustomerImageExpander>
          </div>
        )
      }
    } else {
      content = (
        <a className="attachment_wrapper" href={this.props.attachment.fullUrl} download={this.props.attachment.filename}>
          <img src={`${window.cm.structureUrl}/static/library/images/job_new/attachment.gif`} height={height} width={width} style={imgStyle} />
          <span className="attachment_download icon-down-dir">{truncateSimple(this.props.attachment.filename, 13, this.props.attachment.filename.split('.')[1])}</span>
        </a>
      )
    }

    return (
      <TagName className={classNames("attachment", {[this.props.className]: this.props.className})} style={style}>
        {content}
        {this.props.children}
      </TagName>
    )
  }
}

export function OrderedAttachment({attachment, tagName, index, imageGroup, className}) {
  const TagName = tagName || 'li'
  let content = null

  if (attachment.videoUrl) {
    content = <div className="attachment_wrapper">
      <span className="index_marker">{index}</span>
      <VideoExpander imageGroup={imageGroup} url={attachment.videoUrl} thumbUrl={attachment.smallUrl} />
      <img src={attachment.smallUrl} />
    </div>
  } else if (attachment.isImage) {
    content = <div className="attachment_wrapper">
      <span className="index_marker">{index}</span>
      <ImageExpander imageGroup={imageGroup} href={attachment.fullUrl} downloadHref={attachment.originalDownloadUrl} />
      <img src={attachment.smallUrl} />
    </div>
  } else {
    content = <a className="attachment_wrapper" href={attachment.fullUrl} download={attachment.filename}>
      <span className="index_marker">{index}</span>
      <img src={attachment.pdfUrl} />
      <span className="attachment_download icon-down-dir">{truncateSimple(attachment.filename, 13, attachment.filename.split('.')[1])}</span>
    </a>
  }

  return <TagName className={classNames("attachment", className)}>{content}</TagName>
}

export function CustomerOrderedAttachment({attachment, tagName, index, imageGroup, className}) {
  const TagName = tagName || 'li'
  let content = null

  if (attachment.videoUrl) {
    content = <div className="attachment-wrapper">
      <span className="index-marker">{index}</span>
      <CustomerVideoExpander imageGroup={imageGroup} href={attachment.videoUrl} thumbUrl={attachment.smallUrl} />
      <img src={attachment.smallUrl} />
    </div>
  } else if (attachment.isImage) {
    content = <div className="attachment-wrapper">
      <span className="index-marker">{index}</span>
      <CustomerImageExpander imageGroup={imageGroup} href={attachment.fullUrl}>
        <img src={attachment.smallUrl} />
      </CustomerImageExpander>
    </div>
  } else {
    content = <a className="attachment-wrapper" href={attachment.fullUrl} download={attachment.filename}>
      <span className="index-marker">{index}</span>
      <img src={attachment.pdfUrl} />
      <span className="attachment-download icon-down-dir">{truncateSimple(attachment.filename, 13, attachment.filename.split('.')[1])}</span>
    </a>
  }

  return <TagName className={classNames("attachment", className)}>{content}</TagName>
}

export class Upload extends React.Component {
  buildOverlay(upload) {
    if (this.props.overlayBuilder) {
      return this.props.overlayBuilder(upload)
    } else if (this.props.upload.video) {
      return <VideoExpander url={upload.video.url} />
    } else if (this.props.isImage) {
      return <ImageExpander href={upload.data.fullUrl} />
    }
  }

  render() {
    var uploadSaved = this.props.upload.data && this.props.upload.data.id
    var TagName = this.props.tagName || 'li'
    var infoSection = null
    var overlay = null
    if (this.props.uploading && !(this.props.inError && this.props.percent === 0)) {
      infoSection = <ProgressBar className={this.props.inError ? "error" : ""} percent={this.props.percent} />
    } else if (uploadSaved) {
      overlay = this.buildOverlay(this.props.upload)
    }

    var srcUrl = `${window.cm.structureUrl}/static/library/images/job_new/attachment.gif`
    if (this.props.isImage) {
      srcUrl = this.props.srcUrl
    }

    var indexMarker = null
    if (this.props.index || this.props.index === 0) {
      indexMarker = <span className="index_marker">{this.props.index}</span>
    }

    const className = classNames('upload_item attachment', {
      [this.props.className]: this.props.className,
      'error upload': this.props.inError,
    })
    return (
      <TagName className={className}>
        {indexMarker}
        {overlay}
        {!this.props.readOnly && (
          <a className="remove_attachment icon-cancel-circled" onClick={this.props.onRemove} download={uploadSaved && !this.props.isImage}></a>
        )}
        <img src={srcUrl} />
        {infoSection}
        {this.props.children}
      </TagName>
    )
  }
}
